import { Tooltip, Typography } from '@mui/material';
import type { GridRenderCellParams } from '@mui/x-data-grid-premium';
import React, { useRef, useState } from 'react';
import { SIZES } from '../constants/constants';
import { getTooltipContent, isElementOverflown } from './renderDataGridCellValueUtils';

const TOOLTIP_TITLE_SX = {
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    overflowWrap: 'anywhere'
};

const CELL_ELLIPSIS_SX = {
    whiteSpace: 'nowrap',
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    wordBreak: 'break-all',
    overflowWrap: 'anywhere'
};

const TOOLTIP_SLOT_PROPS = {
    transition: { timeout: 0 }
} as const;

type RenderDataGridCellValueProps = {
    params: GridRenderCellParams;
    /** When true (default), tooltip only if truncated. When false, always show on hover. */
    tooltipOnOverflowOnly?: boolean;
};

export function RenderDataGridCellValue({ params, tooltipOnOverflowOnly = true }: RenderDataGridCellValueProps) {
    const textRef = useRef<HTMLElement | null>(null);
    const [open, setOpen] = useState(false);

    const tooltipTitle = (
        <Typography sx={TOOLTIP_TITLE_SX} fontSize="small">
            {getTooltipContent(params)}
        </Typography>
    );

    if (!tooltipOnOverflowOnly) {
        return (
            <Tooltip title={tooltipTitle} disableInteractive slotProps={TOOLTIP_SLOT_PROPS}>
                <Typography sx={CELL_ELLIPSIS_SX} fontSize={'small'}>
                    {params.formattedValue}
                </Typography>
            </Tooltip>
        );
    }

    function handleOpen() {
        setOpen(Boolean(textRef.current && isElementOverflown(textRef.current)));
    }

    function handleClose() {
        setOpen(false);
    }

    return (
        <Tooltip
            open={open}
            onOpen={handleOpen}
            onClose={handleClose}
            title={tooltipTitle}
            disableInteractive
            slotProps={TOOLTIP_SLOT_PROPS}
        >
            <Typography ref={textRef} sx={CELL_ELLIPSIS_SX} fontSize={SIZES.small}>
                {params.formattedValue}
            </Typography>
        </Tooltip>
    );
}
